<script lang="ts">
    import {
        VpAddIcon,
        VpStarFilledIcon,
        VpSubtractIcon,
    } from '@decathlon-experience/icons/svelte';
    import { createAccordion } from '@melt-ui/svelte';
    import { slide } from 'svelte/transition';

    import { trans } from '$assets/shop-translator.js';
    import Slider from '$saga/components/Slider.svelte';
    import Toggle from '$saga/components/Toggle.svelte';
    import { currency } from '$saga/store/currency';
    import { CurrencyHelper } from '$saga/utils/currency';
    import MultiSelectFilter from '$saga/views/Search/components/verticalFilters/MultiSelectFilter.svelte';
    import { defaultFormValues } from '$saga/views/Search/constants';
    import { filtersContext } from '$saga/views/Search/store';
    import type { FilterType } from '$saga/views/Search/types';
    import {
        findCurrentSelectedFilterLabel,
        getFilterChangeHandler,
        isAlpineSkiingSelected,
    } from '$saga/views/Search/utils';

    import PillarFilterButtons from './PillarFilterButtons.svelte';

    const debouncedHandleFilterChange = getFilterChangeHandler();

    // ab test
    export let openAccordions: boolean = true;
    export let handleSelectedFilter: (filterType: FilterType) => void;

    const {
        elements: { content, item, trigger, root },
        helpers: { isSelected },
    } = createAccordion({
        defaultValue: openAccordions
            ? []
            : ['levels', 'types', 'budget', 'duration', 'resorts', 'tags'],
        multiple: true,
    });

    let selectedSportName: string | null = null;
    let isAlpineSkiingActive = false;

    $: {
        isAlpineSkiingActive = isAlpineSkiingSelected(
            $filtersContext.formValues.theme,
            $filtersContext.alpineSkiingSportId,
        );
        if (
            $filtersContext.formValues.resorts.length > 0 &&
            !isAlpineSkiingActive
        ) {
            $filtersContext.formValues.resorts = [];
        }
        selectedSportName =
            1 === $filtersContext.formValues.theme.length &&
            'all' !== $filtersContext.formValues.theme[0]
                ? findCurrentSelectedFilterLabel(
                      $filtersContext.formValues.theme[0],
                      $filtersContext.defaultSportOptions ?? [],
                  )
                : null;
        if (
            null === selectedSportName &&
            $filtersContext.formValues.exclusiveSport
        ) {
            $filtersContext.formValues.exclusiveSport = false;
        }
    }
</script>

<div {...$root}>
    <div class="flex flex-col gap-m py-m border-y border-border-quiet">
        <PillarFilterButtons {handleSelectedFilter} />
    </div>

    <!--top filters-->
    <div class="text-sm">
        <label
            for="best-reviewed"
            class="flex cursor-pointer items-center gap-s px-s py-m"
        >
            <Toggle
                id="best-reviewed"
                checked={$filtersContext.formValues.bestReviewed}
                onChange={(checked) => {
                    $filtersContext.formValues.bestReviewed = checked;
                    debouncedHandleFilterChange();
                }}
            />
            <span class="flex items-center gap-2xs">
                {trans('search.filter.sidebar.best_reviewed')}
                {#each Array.from({ length: 5 }) as _, idx (idx)}
                    <VpStarFilledIcon
                        size={16}
                        class="text-commercial-catchy"
                    />
                {/each}
            </span>
        </label>

        {#if null !== selectedSportName}
            <label
                for="exclusive-sport"
                class="flex cursor-pointer items-center gap-s px-s py-m"
            >
                <Toggle
                    id="exclusive-sport"
                    checked={$filtersContext.formValues.exclusiveSport}
                    onChange={(checked) => {
                        $filtersContext.formValues.exclusiveSport = checked;
                        debouncedHandleFilterChange();
                    }}
                />
                <span class="flex flex-col">
                    <span>
                        {trans('search.filter.sidebar.exclusive_sport')}
                        {selectedSportName}
                    </span>
                    <span class="text-xs text-content-quiet">
                        {trans('search.filter.sidebar.exclusive_sport_hint')}
                    </span>
                </span>
            </label>
        {/if}
    </div>

    <!--levels-->
    <div {...$item('levels')} use:item>
        <button
            type="button"
            {...$trigger('levels')}
            class="flex w-full items-center justify-between border-t border-solid border-border-quiet py-m"
            use:trigger
        >
            <span class="flex items-center gap-xs">
                {trans('search.filter.sidebar.sport_level')}
                <!--if filter has changed value  -->
                {#if $filtersContext.formValues.levels.length > 0}
                    <div
                        class="size-m rounded-full bg-blue-brand text-xs text-white"
                    >
                        {$filtersContext.formValues.levels.length}
                    </div>
                {/if}
            </span>
            {#if $isSelected('levels')}
                <VpSubtractIcon size={16} />
            {:else}
                <VpAddIcon size={16} />
            {/if}
        </button>
        {#if $isSelected('levels')}
            <div
                class="text-sm"
                {...$content('levels')}
                use:content
                transition:slide
            >
                <button
                    class="w-full text-right text-content-brand underline hover:no-underline"
                    on:click={() => {
                        $filtersContext.formValues.levels =
                            defaultFormValues.levels;
                        debouncedHandleFilterChange();
                    }}>{trans('search.filter.sidebar.clear_all')}</button
                >
                <MultiSelectFilter
                    options={$filtersContext.defaultLevels ?? []}
                    bind:formValue={$filtersContext.formValues.levels}
                />
            </div>
        {/if}
    </div>
    <!--types-->
    <div {...$item('types')} use:item>
        <button
            type="button"
            {...$trigger('types')}
            class="flex w-full items-center justify-between border-t border-solid border-border-quiet py-m"
            use:trigger
        >
            <span class="flex items-center gap-xs">
                {trans('search.filter.sidebar.trip_type')}
                <!--if filter has changed value  -->
                {#if $filtersContext.formValues.types.length > 0}
                    <div
                        class="size-m rounded-full bg-blue-brand text-xs text-white"
                    >
                        {$filtersContext.formValues.types.length}
                    </div>
                {/if}
            </span>
            {#if $isSelected('types')}
                <VpSubtractIcon size={16} />
            {:else}
                <VpAddIcon size={16} />
            {/if}
        </button>
        {#if $isSelected('types')}
            <div
                class="text-sm"
                {...$content('types')}
                use:content
                transition:slide
            >
                <button
                    class="w-full text-right text-content-brand underline hover:no-underline"
                    on:click={() => {
                        $filtersContext.formValues.types =
                            defaultFormValues.types;
                        debouncedHandleFilterChange();
                    }}>{trans('search.filter.sidebar.clear_all')}</button
                >
                <MultiSelectFilter
                    options={$filtersContext.defaultTypes ?? []}
                    bind:formValue={$filtersContext.formValues.types}
                />
            </div>
        {/if}
    </div>

    <!--budget-->
    <div {...$item('budget')} use:item>
        <button
            type="button"
            {...$trigger('budget')}
            class="flex w-full items-center justify-between border-t border-solid border-border-quiet py-m"
            use:trigger
        >
            <span class="flex items-center gap-xs">
                {trans('search.filter.sidebar.budget_per_person')}
                <!--if filter has changed value  -->
                {#if $filtersContext.formValues.budgetMin !== null || $filtersContext.formValues.budgetMax !== null}
                    <div
                        class="size-m rounded-full bg-blue-brand text-xs text-white"
                    >
                        1
                    </div>
                {/if}
            </span>
            {#if $isSelected('budget')}
                <VpSubtractIcon size={16} />
            {:else}
                <VpAddIcon size={16} />
            {/if}
        </button>
        {#if $isSelected('budget')}
            <div
                class="text-sm"
                {...$content('budget')}
                use:content
                transition:slide
            >
                <div class="p-s">
                    <Slider
                        label={CurrencyHelper.getSymbol($currency)}
                        bind:valueMin={$filtersContext.formValues.budgetMin}
                        bind:valueMax={$filtersContext.formValues.budgetMax}
                        defaultMin={$filtersContext.defaultBudgetMin}
                        defaultMax={$filtersContext.defaultBudgetMax}
                        on:change={() => debouncedHandleFilterChange()}
                    />
                </div>
            </div>
        {/if}
    </div>

    <!--duration-->
    <div {...$item('duration')} use:item>
        <button
            type="button"
            {...$trigger('duration')}
            class="flex w-full items-center justify-between border-t border-solid border-border-quiet py-m"
            use:trigger
        >
            <span class="flex items-center gap-xs">
                {trans('search.filter.sidebar.trip_duration')}
                <!--if filter has changed value  -->
                {#if $filtersContext.formValues.durationMin !== null || $filtersContext.formValues.durationMax !== null}
                    <div
                        class="size-m rounded-full bg-blue-brand text-xs text-white"
                    >
                        1
                    </div>
                {/if}
            </span>
            {#if $isSelected('duration')}
                <VpSubtractIcon size={16} />
            {:else}
                <VpAddIcon size={16} />
            {/if}
        </button>
        {#if $isSelected('duration')}
            <div
                class="text-sm"
                {...$content('duration')}
                use:content
                transition:slide
            >
                <div class="p-s">
                    <Slider
                        label={trans('search.filter.sidebar.days_abbr')}
                        bind:valueMin={$filtersContext.formValues.durationMin}
                        bind:valueMax={$filtersContext.formValues.durationMax}
                        defaultMin={$filtersContext.defaultDurationMin}
                        defaultMax={$filtersContext.defaultDurationMax}
                        on:change={() => debouncedHandleFilterChange()}
                    />
                </div>
            </div>
        {/if}
    </div>

    {#if isAlpineSkiingActive && ($filtersContext.defaultSkiResorts?.length ?? 0) > 0}
        <div {...$item('resorts')} use:item>
            <button
                type="button"
                {...$trigger('resorts')}
                class="flex w-full items-center justify-between border-t border-solid border-border-quiet py-m"
                use:trigger
            >
                <span class="flex gap-xs">
                    {trans('search.filter.sidebar.resorts')}
                    {#if $filtersContext.formValues.resorts.length > 0}
                        <div
                            class="mb-auto size-m rounded-full bg-blue-brand text-xs text-white"
                        >
                            {$filtersContext.formValues.resorts.length}
                        </div>
                    {/if}
                </span>
                {#if $isSelected('resorts')}
                    <VpSubtractIcon size={16} />
                {:else}
                    <VpAddIcon size={16} />
                {/if}
            </button>
            {#if $isSelected('resorts')}
                <div
                    class="text-sm"
                    {...$content('resorts')}
                    use:content
                    transition:slide
                >
                    <button
                        class="w-full text-right text-content-brand underline hover:no-underline"
                        on:click={() => {
                            $filtersContext.formValues.resorts =
                                defaultFormValues.resorts;
                            debouncedHandleFilterChange();
                        }}>{trans('search.filter.sidebar.clear_all')}</button
                    >
                    <MultiSelectFilter
                        options={$filtersContext.defaultSkiResorts ?? []}
                        bind:formValue={$filtersContext.formValues.resorts}
                        visibleLimit={5}
                    />
                </div>
            {/if}
        </div>
    {/if}

    <!--tags-->
    <div {...$item('tags')} use:item>
        <button
            type="button"
            {...$trigger('tags')}
            class="flex w-full items-center justify-between border-t border-solid border-border-quiet py-m"
            use:trigger
        >
            <span class="flex gap-xs">
                {trans('search.filter.sidebar.theme')}
                <!--if filter has changed value  -->
                {#if $filtersContext.formValues.tags.length > 0}
                    <div
                        class="mb-auto size-m rounded-full bg-blue-brand text-xs text-white"
                    >
                        {$filtersContext.formValues.tags.length}
                    </div>
                {/if}
            </span>
            {#if $isSelected('tags')}
                <VpSubtractIcon size={16} />
            {:else}
                <VpAddIcon size={16} />
            {/if}
        </button>
        {#if $isSelected('tags')}
            <div
                class="text-sm"
                {...$content('tags')}
                use:content
                transition:slide
            >
                <button
                    class="w-full text-right text-content-brand underline hover:no-underline"
                    on:click={() => {
                        $filtersContext.formValues.tags =
                            defaultFormValues.tags;
                        debouncedHandleFilterChange();
                    }}>{trans('search.filter.sidebar.clear_all')}</button
                >
                <MultiSelectFilter
                    options={$filtersContext.defaultTags ?? []}
                    bind:formValue={$filtersContext.formValues.tags}
                />
            </div>
        {/if}
    </div>
</div>
